/* eslint-disable */
import axios from 'axios';
import { toast } from 'react-toastify';
import cookies from 'js-cookie';
import { AxiosError, InternalAxiosRequestConfig } from 'axios';

import AuthService from './authService';
const isBrowser = typeof window !== 'undefined';

export const _getTokenFromStorage = (key: any) => isBrowser && cookies.get(key);

const baseURL = import.meta.env.VITE_BASEURL as string;

const instance = axios.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- Refresh token handling ---
let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

instance.interceptors.request.use(
  (config) => {
    const storeId = localStorage.getItem('storeId');
    if (storeId) {
      config.headers['x-store-id'] = storeId;
    }
    const token = _getTokenFromStorage('accessToken');
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = AuthService.getRefreshToken();

      if (refreshToken) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (originalRequest.headers) {
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
              }
              return instance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const res = await axios.post(
            `${import.meta.env.VITE_BASEURL}auth/refresh-token`,
            { refreshToken }
          );

          if (res.data?.succeded) {
            const data = res.data.data;

            AuthService.setAccessToken(data.accessToken);
            AuthService.setAccessEmail(data.email);
            AuthService.setRefreshToken(data.refreshToken);

            processQueue(null, data.accessToken);

            if (originalRequest.headers) {
              originalRequest.headers['Authorization'] =
                `Bearer ${data.accessToken}`;
            }
            return instance(originalRequest);
          } else {
            processQueue('Refresh failed', null);
            throw new Error('Refresh failed');
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          AuthService.deleteAccessToken();
          AuthService.deleteRefreshToken();
          AuthService.deleteAccessEmail();
          toast('Unauthorized or Expired Session, Login from Navigation Panel');
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        AuthService.deleteAccessToken();
        AuthService.deleteRefreshToken();
        AuthService.deleteAccessEmail();
        toast('Unauthorized or Expired Session, Login from Navigation Panel');
      }
    }

    return Promise.reject(error);
  }
);

export default instance;
